/**
 * Auth resolution.
 *
 * Resolution order:
 * 1. --token flag (API token) — takes precedence
 * 2. ELECTRIC_API_TOKEN env var (API token)
 * 3. ~/.config/electric/auth.json (user JWT) — checked for expiration
 * 4. No auth — fail with guidance
 */

import { loadStoredAuth, clearStoredAuth } from './config.js'

export interface ResolvedAuth {
  token: string
  type: `api_token` | `jwt`
}

let explicitToken: string | undefined

export function setExplicitToken(token: string): void {
  explicitToken = token
}

/** @internal — for testing only */
export function _resetExplicitToken(): void {
  explicitToken = undefined
}

const EXPIRY_WARNING_MS = 24 * 60 * 60 * 1000 // 24 hours

export async function resolveAuth(): Promise<ResolvedAuth> {
  // 1. Explicit --token flag
  if (explicitToken) {
    return { token: explicitToken, type: `api_token` }
  }

  // 2. API token from env
  const apiToken = process.env.ELECTRIC_API_TOKEN
  if (apiToken) {
    return { token: apiToken, type: `api_token` }
  }

  // 3. Stored JWT
  const stored = await loadStoredAuth()
  if (stored) {
    const expiresAt = new Date(stored.expiresAt)
    const now = new Date()

    if (expiresAt < now) {
      await clearStoredAuth()
      throw new AuthError(
        `Session expired. Run 'electric auth login' to re-authenticate.`
      )
    }

    if (expiresAt.getTime() - now.getTime() < EXPIRY_WARNING_MS) {
      const hours = Math.ceil(
        (expiresAt.getTime() - now.getTime()) / (60 * 60 * 1000)
      )
      process.stderr.write(
        `Warning: Session expires in ${hours} hours. Run 'electric auth login' to refresh.\n`
      )
    }

    return { token: stored.token, type: `jwt` }
  }

  // 4. No auth
  throw new AuthError(
    `Not authenticated. Pass --token, set ELECTRIC_API_TOKEN, or run 'electric auth login'.`
  )
}

export class AuthError extends Error {
  constructor(message: string) {
    super(message)
    this.name = `AuthError`
  }
}
